import classNames from 'classnames'
import { Fragment, useId } from 'react'
import { visuallyHidden } from '../../styles/utilities.css.ts'
import * as styles from './segmented-control.css.ts'

export type SegmentedControlOption<TValue extends string> = {
  value: TValue
  label: string
  disabled?: boolean
  /**
   * Why this option cannot be chosen. A disabled button is silent about its reason, so the text is
   * rendered off-screen and pointed at with `aria-describedby`, and repeated as the hover `title`.
   */
  disabledReason?: string
}

export type SegmentedControlProps<TValue extends string> = {
  options: SegmentedControlOption<TValue>[]
  value: TValue
  onChange: (value: TValue) => unknown
  /** Names the group for assistive technology; the visible label, if any, lives outside. */
  ariaLabel: string
  className?: string
}

/** A single-choice control laid out as pill segments on one track. */
export const SegmentedControl = <TValue extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: SegmentedControlProps<TValue>) => {
  const reasonIdPrefix = useId()
  return (
    <div className={classNames(styles.track, className)} role="group" aria-label={ariaLabel}>
      {options.map(option => {
        const reasonId = option.disabled && option.disabledReason ? `${reasonIdPrefix}-${option.value}` : undefined
        return (
          <Fragment key={option.value}>
            <button
              type="button"
              className={styles.segment}
              aria-pressed={option.value === value}
              disabled={option.disabled}
              aria-describedby={reasonId}
              title={reasonId ? option.disabledReason : undefined}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
            {/* Outside the button on purpose: inside, the reason would join the button's accessible
                name instead of describing it. */}
            {reasonId && (
              <span className={visuallyHidden} id={reasonId}>
                {option.disabledReason}
              </span>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
